import { addClass, removeClass, hide, getQueryString, getCurrentLanguageId, show } from "../../common";
import { FilterParamItem } from "../filter-param-items";
import HttpClient from "../../http-client";

export default class SizeOption {
    private input: HTMLInputElement;
    private onChange: () => void;
    private selectedClass = 'selected';
    private isHidden: boolean;
    private pageId: number;
    private sublevelElement: HTMLElement;
    private filterUpdated: () => void;
    private paramId: number;
    children: SizeChildOption[] = [];
    childrenLoaded: boolean;

    private _listId: string;
    get listId(): string {
        return this._listId;
    }

    private _isSelected: boolean;

    get isSelected(): boolean {
        return this._isSelected;
    }

    private _name: string;
    get name(): string {
        return this._name;
    }

    private _isMainItem: boolean;
    get isMainItem(): boolean {
        return this._isMainItem;
    }

    private _sortValue: number;
    get sortValue(): number{
        return this._sortValue;
    }

    private _element: HTMLElement;
    get element(): HTMLElement{
        return this._element;
    }

    //SUBLEVEL
    private async loadSublevel(): Promise<void> {
        const items = await this.loadChildren();
        if (items && items.length > 0) {
            this.children = this.createOptions(items);
            this.renderChildren();
        }
        this.childrenLoaded = true;
    }

    private createOptions(items: FilterParamItem[]): SizeChildOption[] {
        return items.map(item =>
            new SizeChildOption(item, this, () => this.filterUpdated()));
    }

    private renderChildren(): void {
        if (!this.children || this.children.length === 0) {
            this.displaySublevel(false);
            return;
        }

        if (!this.sublevelElement) {
            this.sublevelElement = this.createSublevelElement(this.name, this.listId);
        }

        this.children.forEach(o => this.sublevelElement.appendChild(o.element));
        this.element.parentElement.parentElement.appendChild(this.sublevelElement);
    }

    private async loadChildren(): Promise<FilterParamItem[]> {
        const urlEndpoint = `/filters/getlistitemchildren/${this.paramId}/${this.listId}`;
        const queryString = getQueryString();
        const url = queryString ? `${urlEndpoint}?${queryString}` : urlEndpoint;

        const response = await new HttpClient().fetch(url, {
            headers: {
                'PageId': this.pageId.toString(),
                'Current-Language-Id': getCurrentLanguageId()
            }
        });
        const items: FilterParamItem[] = JSON.parse(await response.json());
        return items;
    }

    private displaySublevel(display: boolean = true): void {
        if (!this.sublevelElement) {
            return;
        }

        if (display && this.children.length > 0) {
            show(this.sublevelElement);
        } else {
            hide(this.sublevelElement);
        }
    }

    private createSublevelElement(sizeName: string, parentId: string): HTMLElement {
        const element = document.createElement('div');
        element.setAttribute('data-subsize-id', parentId);
        const header = document.createElement('h5');
        header.innerHTML = sizeName;
        element.appendChild(header);
        return element;
    }

    private async handleFirstLevelOptionClick(evt: Event): Promise<void> {
        evt.preventDefault();
        this.filterUpdated();
    }

    // END SUBLEVEL
    public static CreateOptionElement(optionItem: FilterParamItem): HTMLElement {
        const div = document.createElement('div');

        div.classList.add('textile-sizes__item');
        div.setAttribute('data-component-part', 'item-box');
        div.setAttribute('data-is-main-item', 'true');
        div.setAttribute('data-list-id', optionItem.ListId.toString());
        div.setAttribute('data-sort', optionItem.Sort.toString());

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = optionItem.ListId.toString();
        input.name = optionItem.Name;
        div.appendChild(input);
        div.innerText = optionItem.Name;

        return div;
    }

    constructor(baseElement: HTMLElement, pageId: number, paramId: number, onChange: () => void) {
        this._element = baseElement;
        this.onChange = onChange;
        this.pageId = pageId;
        this.filterUpdated = onChange;
        this.paramId = paramId;
        baseElement.addEventListener('click', (evt: Event) => this.handleFirstLevelOptionClick(evt));
        this.init();
    }

    public updateItems(items: FilterParamItem[]) {
        const item = items.find(x => x.ListId.toString() === this.listId);

        item ? this.show() : this.hide();
    }

    private init() {
        this.input = this._element.querySelector('input') as HTMLInputElement;
        this._listId = this._element.dataset.listId;
        this._name = this.input.name;
        this._isMainItem = this.input.dataset.isMainItem === 'true';
        this._sortValue = Number(this._element.dataset.sort);
        if (this._isSelected) {
            addClass(this._element, this.selectedClass);
        }

        this._element.addEventListener('click', () => this.toggleSelection());
    }

    private async toggleSelection() {
        if (this._isSelected) {
            this.unselect();
        } else {
            await this.select();
        }
        this.onChange();
    }

    public async select(selectChildren: boolean = true) : Promise<void>{
        this.input.checked = true;
        addClass(this._element, this.selectedClass);

        // load subsizes
        if (!this.childrenLoaded) {
            await this.loadSublevel();
        }
        this.displaySublevel(true);

        // check all unselected subsizes
        if (selectChildren) {
            this.children
                .filter(o => !o.isSelected)
                .forEach(o => o.select());
        }

        this._isSelected = true;
    }

    public unselect() {
        removeClass(this._element, this.selectedClass);
        this.input.checked = false;
        this._isSelected = false;

        if (this.childrenLoaded) {
            this.children.forEach(o => o.unselect());
        }

        this.displaySublevel(false);
    }

    public hide(): void {
        hide(this._element);
        this.isHidden = true;
    }

    public update(items: FilterParamItem[]){
        const item = items.find(x => x.ListId.toString() === this.listId);
        item ? this.show() : this.hide();
    }

    private show(): void {
        this.element.removeAttribute('style');
        this.isHidden = false;
    }

}

export class SizeChildOption {
    private _isSelected: boolean = false;
    element: HTMLElement;
    parent: SizeOption;
    private item: FilterParamItem;
    private isHidden: boolean;

    get listId(): string {
        return this.item.ListId.toString();
    }

    get name(): string {
        return this.item.Name;
    }

    get sortValue(): number {
        return this.item.Sort;
    }

    get isSelected(): boolean {
        return this._isSelected;
    }

    private filterUpdated: () => void;

    constructor(item: FilterParamItem, parent: SizeOption, onFilterUpdated: () => void) {
        this.item = item;
        this.parent = parent;
        this.filterUpdated = onFilterUpdated;

        this.element = this.createSizeInputElement()
    }

    public select(): void {
        const linkElement = this.element.querySelector('span');
        addClass(linkElement, 'selected-size-fake-link');
        this.element.querySelector('input').checked = true;
        this._isSelected = true;
    }

    public unselect(): void {
        const linkElement = this.element.querySelector('span');
        removeClass(linkElement, 'selected-size-fake-link');
        this.element.querySelector('input').checked = false;
        this._isSelected = false;
    }

    public update(items: FilterParamItem[]): void {
        const item = items.find(x => x.ListId === this.item.ListId);
        item && this.parent.isSelected ? this.show() : this.hide();
    }

    public hide() {
        hide(this.element);
        this.isHidden = true;
    }

    private show() {
        show(this.element);
        this.isHidden = false;
    }

    private createSizeInputElement(): HTMLElement {
        const div = document.createElement('div');
        div.classList.add('color-input');

        const label = document.createElement('label');
        label.dataset.listId = this.listId;
        div.appendChild(label);

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.classList.add('color-checkbox-input');
        input.dataset.listId = this.listId;
        input.dataset.parentListId = this.parent.listId;
        input.name = this.name;
        input.checked = this._isSelected;
        input.addEventListener('change', (evt: Event) => this.handleCheckboxChange(evt));
        label.appendChild(input);

        const span = document.createElement('span');
        span.classList.add('color-fake-link');
        if (this._isSelected) {
            span.classList.add('selected-color-fake-link');
        }
        span.dataset.listId = this.listId;
        span.dataset.parentListId = this.parent.listId;
        span.innerText = this.name;
        span.addEventListener('click', (evt: Event) => this.handleSubsizeLinkClick(evt));
        label.appendChild(span);

        return div;
    }

    private unselectSiblings() {
        const siblings = this.parent.children.filter(o => o.listId !== this.listId);
        siblings.forEach(o => o.unselect());
    }

    private handleSubsizeLinkClick(evt: Event): any {
        evt.preventDefault();
        this.unselectSiblings();
        this.select();

        this.filterUpdated();
    }

    private handleCheckboxChange(evt: Event): void {
        const element = evt.target as HTMLInputElement;
        if (element.checked) {
            this.select();
        } else {
            this.unselect();
        }

        this.filterUpdated();
    }
}

